/**
 * Build a rich iso sprite generation prompt for a mob.
 *
 * The char path (IsoSpritePromptCard) uses race/class/equipment/appearance.
 * Monsters don't have those — but they do have creature type, size, primary
 * action, and sometimes an explicit imgPrompt from an encounter template.
 * This helper folds those into the same SpriteGenParams shape the API expects.
 */

import type { EncounterMobSpec } from '@machina/core';
import type { Mob } from '../stores/mobStore';

export interface MobSpriteParams {
  /** Stable UUID for this mob instance — sprite cache key */
  id: string;
  name: string;
  race?: string;
  className?: string;
  appearance?: string;
  isNpc?: boolean;
}

/** Derive a short fighter-type label from the mob's primary action */
function classFromActions(mob: Mob): string {
  const primary = mob.actions?.[0];
  if (!primary) return 'monstrous creature';

  const range = primary.range === 'ranged' ? 'ranged' : 'melee';
  const dmgType = primary.damageType && primary.damageType !== 'slashing' && primary.damageType !== 'piercing' && primary.damageType !== 'bludgeoning'
    ? primary.damageType
    : null;
  const weapon = primary.name.toLowerCase();

  return dmgType
    ? `${range} ${dmgType} attacker wielding ${weapon}`
    : `${range} fighter wielding ${weapon}`;
}

/** Build a visual fallback when no imgPrompt hint is available */
function fallbackAppearance(mob: Mob): string {
  const size = mob.size || 'medium';
  const type = mob.type || 'creature';
  return `${size} ${type}, detailed monster design`;
}

export function buildMobSpriteParams(
  mob: Mob,
  spec?: EncounterMobSpec | null,
): MobSpriteParams {
  // Display name preserves instance number ("Fire Newt Warrior 2") since each
  // instance now gets its OWN sprite — the number helps FLUX vary the output.
  const name = mob.displayName || mob.name;
  const creatureType = mob.type || 'creature';
  const classHint = classFromActions(mob);
  const appearance = spec?.imgPrompt ?? fallbackAppearance(mob);

  return {
    id: mob.id,
    name,
    race: creatureType,
    className: classHint,
    appearance,
    isNpc: true,
  };
}

